import PropTypes from "prop-types";
import * as React from "react";
import { Chip, Spinner, TextInput, Typography } from "../../themeV2";
import { useDebounce } from "../../hooks";
import { useUserSearch } from "../Dashboard/AdvancedSettings/hooks";
import UserInvite from "../UserInvite";
import { formatPhoneNumber } from "../../util";
import styles from "./AddShopDialog.module.css";
import { ACTION } from "./addShopReducer";

function OwnerSection({ ownerEmail, ownerUser, ownerStatus, dispatch }) {
  const [debouncedEmail, setDebouncedEmail] = React.useState("");

  useDebounce(
    () => {
      setDebouncedEmail(ownerEmail);
    },
    500,
    [ownerEmail],
  );

  const { users, isLoading } = useUserSearch(debouncedEmail);

  React.useEffect(() => {
    if (isLoading) {
      dispatch({
        type: ACTION.SET_OWNER,
        user: null,
        status: "loading",
      });
      return;
    }

    if (!debouncedEmail || debouncedEmail.trim().length < 4) {
      dispatch({ type: ACTION.SET_OWNER, user: null, status: "idle" });
      return;
    }

    const exactMatch = users?.find(
      (u) => u.email.toLowerCase() === debouncedEmail.trim().toLowerCase(),
    );

    if (exactMatch) {
      dispatch({
        type: ACTION.SET_OWNER,
        user: exactMatch,
        status: "found",
      });
    } else {
      dispatch({
        type: ACTION.SET_OWNER,
        user: null,
        status: "not_found",
      });
    }
  }, [users, isLoading, debouncedEmail, dispatch]);

  return (
    <section className={styles.section}>
      <Typography variant="title-md" as="h3">
        Shop owner
      </Typography>

      <TextInput
        label="Owner Email"
        required
        type="email"
        value={ownerEmail}
        onChange={(e) =>
          dispatch({
            type: ACTION.SET_FIELD,
            field: "ownerEmail",
            value: e.target.value,
          })
        }
        trailing={
          isLoading ? <Spinner size="sm" label="Searching for user" /> : null
        }
      />

      <div aria-live="polite">
        {ownerStatus === "found" && ownerUser && (
          <div className={styles.userResultColumn}>
            <div className={styles.userResult}>
              <Chip variant="assist" size="sm" label="User found" />
              <Typography variant="body-lg">
                {ownerUser.first_name} {ownerUser.last_name}
              </Typography>
            </div>
            {ownerUser.phone_number && (
              <Typography variant="label-md" color="variant">
                Phone: {formatPhoneNumber(ownerUser.phone_number)}
              </Typography>
            )}
          </div>
        )}

        {ownerStatus === "not_found" && (
          <div className={styles.notFoundRow}>
            <Typography variant="body-lg" color="error">
              No user found with that exact email.
            </Typography>
            <UserInvite size="small" variant="outlined" label="Invite User" />
          </div>
        )}
      </div>
    </section>
  );
}

OwnerSection.propTypes = {
  ownerEmail: PropTypes.string.isRequired,
  ownerUser: PropTypes.object,
  ownerStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default React.memo(OwnerSection);
